/** Formatting helpers shared across the customer dashboard. Kept as plain
 * functions (no dependency) since the formatting needs here are small and
 * fixed (US-style phone numbers, cents-as-dollars, relative timestamps). */

export function formatCurrencyFromCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "—";
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

export function formatPercent(fraction: number | null | undefined): string {
  if (fraction === null || fraction === undefined || Number.isNaN(fraction)) return "—";
  return `${Math.round(fraction * 100)}%`;
}

/** Best-effort US phone formatting; falls back to the raw value for
 * anything that doesn't look like a 10/11-digit US number (e.g. already
 * formatted, or a non-US number). */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return "—";
  const digits = phone.replace(/\D/g, "");
  const tenDigit = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (tenDigit.length !== 10) return phone;
  return `(${tenDigit.slice(0, 3)}) ${tenDigit.slice(3, 6)}-${tenDigit.slice(6)}`;
}

/** SQLite's `datetime('now')` / `date('now')` produce naive UTC strings
 * with no timezone suffix (e.g. "2026-09-24 14:03:00"). `new Date(...)`
 * parses that as *local* time in most JS engines, which silently skews
 * every timestamp by the viewer's UTC offset. Normalizing to an explicit
 * "Z" first ensures it's parsed as UTC, matching how it was written. */
export function parseSqliteTimestamp(value: string): Date {
  const normalized = value.includes("T") ? value : `${value.replace(" ", "T")}Z`;
  return new Date(normalized);
}

export function formatRelativeTime(value: string): string {
  const date = parseSqliteTimestamp(value);
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.round(diffMs / 1000);

  if (diffSec < 60) return "just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;
  const diffDay = Math.round(diffHour / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function formatDateTime(value: string): string {
  const date = parseSqliteTimestamp(value);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}
